import React from 'react';

/**
 * Preview component for PDF Offer - Shows exactly how the PDF will look
 * This is a visual mockup that will be converted to actual PDF using WeasyPrint on backend
 */
const OfferPDFPreview = ({ 
  // Intermediary data
  intermediaryData = {
    companyName: "EXPERT CREDIT ADVISOR SRL",
    cui: "RO12345678",
    jNumber: "J40/1234/2023",
    address: "București, Str. Exemplu nr. 10, Sector 1",
    phone: "0712 345 678",
    email: "office@expertcredit.ro",
    website: "www.expertcredit.ro",
    logoUrl: null
  },
  // Client data
  clientData = {
    fullName: "POPESCU ION VASILE",
    cnp: "1850101123456"
  },
  // Credit parameters
  creditParams = {
    propertyValueEuro: 150000,
    propertyValueRon: 750000,
    creditValueRon: 600000,
    downPaymentRon: 150000,
    periodYears: 30,
    periodMonths: 360,
    maxDebtRatio: "40%/45%",
    creditType: "Credit Ipotecar",
    propertyType: "apartament" // "apartament" sau "casa"
  },
  // Bank offers array - trebuie să vină exact din calculator cu toate detaliile
  bankOffers = [],
  // Generation date
  generationDate = new Date().toLocaleDateString('ro-RO')
}) => {
  return (
    <div className="bg-white p-8 max-w-[1100px] mx-auto text-sm" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div className="border-b-2 border-gray-300 pb-4 mb-4">
        <h1 className="text-center text-lg font-bold text-gray-800 mb-2">
          Anexa 4 - Simulare Soluții de Creditare
        </h1>
        <p className="text-xs text-gray-600 text-center italic">
          Document informativ – nu constituie consiliere financiară și nu reprezintă oferte de credit conform OUG 52/2016.
        </p>
        <p className="text-xs text-gray-600 text-center">
          Soluțiile sunt efectuate pe baza informațiilor pe care ni le-ați oferit și obținute în urma simulării creditelor pe calculatorul din platforma <strong>FINBRO</strong>.
        </p>
      </div>

      {/* Company and Client Info */}
      <div className="flex justify-between items-start mb-6">
        {/* Left - Company Logo and Info */}
        <div className="flex items-start gap-4">
          {intermediaryData.logoUrl ? (
            <img src={intermediaryData.logoUrl} alt="Logo" className="w-20 h-20 object-contain" />
          ) : (
            <div className="w-20 h-20 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold text-xs text-center px-1">{intermediaryData.companyName.substring(0, 15)}</span>
            </div>
          )}
          <div className="text-xs">
            <p className="font-bold text-gray-900">{intermediaryData.companyName}</p>
            <p className="text-gray-600">CUI: {intermediaryData.cui}</p>
            <p className="text-gray-600">Nr. Reg.: {intermediaryData.jNumber}</p>
            <p className="text-gray-600">{intermediaryData.phone}</p>
            <p className="text-gray-500 text-[10px] mt-1">Intermediar de Credite</p>
          </div>
        </div>

        {/* Right - Credit Parameters */}
        <div className="text-right text-xs">
          <p className="font-bold text-gray-900 mb-1">OFERTĂ ÎNTOCMITĂ PENTRU:</p>
          <p className="text-blue-700 font-bold text-base mb-2">{clientData.fullName}</p>
          
          <div className="bg-gray-50 p-2 rounded border">
            <p><strong>Valoarea proprietății:</strong> {creditParams.propertyValueEuro?.toLocaleString()} EUR / {creditParams.propertyValueRon?.toLocaleString()} RON</p>
            <p><strong>Valoare credit:</strong> {creditParams.creditValueRon?.toLocaleString()} RON</p>
            <p><strong>Avans din surse proprii:</strong> {creditParams.downPaymentRon?.toLocaleString()} RON</p>
            <p><strong>Perioada creditare:</strong> {creditParams.periodYears} Ani ({creditParams.periodMonths} Luni)</p>
            <p><strong>Tip imobil:</strong> {creditParams.propertyType === 'casa' ? 'Casă' : 'Apartament'}</p>
            <p><strong>Grad de îndatorare maxim:</strong> {creditParams.maxDebtRatio}</p>
          </div>
        </div>
      </div>

      {/* Main Comparison Table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-blue-800 text-white">
              <th className="border border-gray-400 p-2 text-left font-semibold w-40">Tip ofertă</th>
              {bankOffers.map((offer, idx) => (
                <th key={idx} className="border border-gray-400 p-2 text-center font-normal">
                  {offer.creditType || creditParams.creditType}
                </th>
              ))}
            </tr>
            <tr className="bg-blue-700 text-white">
              <th className="border border-gray-400 p-2 text-left font-semibold">Nume Bancă / Produs</th>
              {bankOffers.map((offer, idx) => (
                <th key={idx} className="border border-gray-400 p-2 text-center font-semibold text-xs">
                  {offer.bankName}
                  {offer.productName && <div className="font-normal text-blue-200 text-[10px]">{offer.productName}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Dobanda fixa */}
            <tr className="bg-white">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">Dobândă fixă</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center font-bold text-blue-700">
                  {offer.fixedRate || '-'}
                </td>
              ))}
            </tr>
            {/* Dobanda variabila */}
            <tr className="bg-gray-50">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">Dobândă variabilă</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.variableRate || '-'}
                </td>
              ))}
            </tr>
            {/* IRCC */}
            <tr className="bg-white">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">IRCC</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.ircc || '-'}
                </td>
              ))}
            </tr>
            {/* Rata lunara - dobanda fixa (fara asigurare) */}
            <tr className="bg-yellow-50">
              <td className="border border-gray-400 p-2 font-semibold bg-yellow-100">Rată lunară - dobândă fixă (fără asig. viață)</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.monthlyPaymentFixed || '-'}
                </td>
              ))}
            </tr>
            {/* Rata lunara - dobanda fixa (cu asigurare) */}
            <tr className="bg-yellow-50">
              <td className="border border-gray-400 p-2 font-semibold bg-yellow-100">Rată lunară - dobândă fixă (cu asig. viață)</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center font-bold">
                  {offer.monthlyPaymentFixedWithInsurance || '-'}
                </td>
              ))}
            </tr>
            {/* Rata lunara - dobanda variabila (fara asigurare) */}
            <tr className="bg-yellow-50">
              <td className="border border-gray-400 p-2 font-semibold bg-yellow-100">Rată lunară - dobândă variabilă (fără asig. viață)</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.monthlyPaymentVariable || '-'}
                </td>
              ))}
            </tr>
            {/* Rata lunara - dobanda variabila (cu asigurare) */}
            <tr className="bg-yellow-50">
              <td className="border border-gray-400 p-2 font-semibold bg-yellow-100">Rată lunară - dobândă variabilă (cu asig. viață)</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center font-bold">
                  {offer.monthlyPaymentVariableWithInsurance || '-'}
                </td>
              ))}
            </tr>
            {/* DAE */}
            <tr className="bg-orange-50">
              <td className="border border-gray-400 p-2 font-semibold bg-orange-100">DAE (Dobândă Anuală Efectivă)</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center font-semibold text-orange-700">
                  {offer.dae || '-'}
                </td>
              ))}
            </tr>
            {/* Comision analiza */}
            <tr className="bg-white">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">Comision analiză</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.analysisCommission || '-'}
                </td>
              ))}
            </tr>
            {/* Comision administrare */}
            <tr className="bg-gray-50">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">Comision administrare</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.adminCommission || '-'}
                </td>
              ))}
            </tr>
            {/* Taxa arhiva */}
            <tr className="bg-white">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">Taxă arhivă</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.archiveFee || '-'}
                </td>
              ))}
            </tr>
            {/* Taxa evaluare Apartament */}
            <tr className="bg-orange-50">
              <td className="border border-gray-400 p-2 font-semibold bg-orange-100">
                Taxă evaluare Apartament
              </td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.evaluationFeeApartment || offer.evaluationFee || '-'}
                </td>
              ))}
            </tr>
            {/* Taxa evaluare Casa */}
            <tr className="bg-orange-50">
              <td className="border border-gray-400 p-2 font-semibold bg-orange-100">
                Taxă evaluare Casă
              </td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.evaluationFeeHouse || offer.evaluationFee || '-'}
                </td>
              ))}
            </tr>
            {/* Asigurare de viata */}
            <tr className="bg-white">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">Asigurare de viață</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.lifeInsurance || '-'}
                </td>
              ))}
            </tr>
            {/* PAD - Asigurare obligatorie imobil */}
            <tr className="bg-purple-50">
              <td className="border border-gray-400 p-2 font-semibold bg-purple-100">PAD - Asigurare obligatorie</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.padInsurance || offer.propertyInsurancePAD || '-'}
                </td>
              ))}
            </tr>
            {/* Asigurare facultativa imobil */}
            <tr className="bg-purple-50">
              <td className="border border-gray-400 p-2 font-semibold bg-purple-100">Asigurare facultativă imobil</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.optionalPropertyInsurance || offer.propertyInsuranceOptional || '-'}
                </td>
              ))}
            </tr>
            {/* Comision rambursare anticipata */}
            <tr className="bg-white">
              <td className="border border-gray-400 p-2 font-semibold bg-gray-100">Comision rambursare anticipată</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center">
                  {offer.earlyRepaymentCommission || '-'}
                </td>
              ))}
            </tr>
            {/* Valabilitate preaprobare */}
            <tr className="bg-blue-50">
              <td className="border border-gray-400 p-2 font-semibold bg-blue-100">Valabilitate preaprobare</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center font-semibold">
                  {offer.preApprovalValidity || '-'}
                </td>
              ))}
            </tr>
            {/* Cost total credit */}
            <tr className="bg-green-50">
              <td className="border border-gray-400 p-2 font-bold bg-green-100">COST TOTAL CREDIT</td>
              {bankOffers.map((offer, idx) => (
                <td key={idx} className="border border-gray-400 p-2 text-center font-bold text-green-700">
                  {offer.totalCost || '-'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Disclaimer Section */}
      <div className="bg-gray-50 p-4 rounded border mb-4 text-xs">
        <p className="mb-1">Aceste oferte sunt obținute potrivit simulărilor efectuate pe calculatorul <strong>FINBRO</strong>.</p>
        <p className="mb-1">Soluțiile sunt furnizate în funcție de datele obținute din Formularul Informații Client.</p>
        <p className="mb-1">În funcție de prescoringul și decizia băncii, precum și de alți factori, aceste oferte se pot modifica.</p>
        <p className="font-semibold text-red-700 mt-2">
          Acest document are caracter de prezentare și nu reprezintă un act legal, sau o aprobare a creditului.
        </p>
      </div>

      {/* Consumer Information */}
      <div className="border rounded p-4 mb-4 text-xs">
        <h3 className="font-bold text-gray-900 mb-2">Informare consumator:</h3>
        <ul className="list-disc pl-5 space-y-1 text-gray-700">
          <li>Aveți posibilitatea de a alege una sau mai multe instituții financiare de la care să solicitați oferte personalizate cazului dumneavoastră, în vederea obținerii informațiilor necesare conform OUG 52/2016.</li>
          <li><strong>DAE</strong> – Dobânda anuală efectivă, reprezintă costul total al împrumutului exprimat ca procentaj anual și reprezintă costul total al creditului.</li>
          <li><strong>IRCC</strong> – Indicele de referință pentru creditele acordate consumatorilor, calculat trimestrial conform prevederilor legale și publicat pe site-ul oficial al BNR.</li>
          <li>La plata anticipată a creditului, fie integrală fie parțială, nu se percep costuri/penalități.</li>
          <li>Aveți la dispoziție o perioadă de reflecție de 15 zile calendaristice pentru analiza deciziei semnării contractului de credit. Puteți renunța la aceasta perioadă în baza unei declarații scrise.</li>
          <li>Prin contractarea unui credit cu dobândă variabilă vă supuneți riscului de rată a dobânzii, ce vă poate afecta posibilitatea de plată.</li>
          <li>Veniturile dumneavoastră pot varia, astfel trebuie să vă asigurați că veți putea achita ratele lunare în cazul în care venitul va scădea.</li>
          <li>Neplata obligațiilor scadente conduce la majorarea sumelor datorate și în ultima instanță se poate ajunge la executare silită.</li>
        </ul>
      </div>

      {/* Client Selection Section - NEW */}
      <div className="border-2 border-blue-300 rounded-lg p-4 mb-6 bg-blue-50">
        <h3 className="font-bold text-gray-900 mb-3 text-center">SOLICITARE OFERTE DE CREDIT</h3>
        <div className="text-sm mb-4">
          <p>
            Subsemnatul/Subsemnata <span className="inline-block border-b border-gray-400 min-w-[250px] mx-1 font-semibold">{clientData.fullName}</span>, 
            solicit obținerea următoarei/următoarelor oferte de credit:
          </p>
        </div>
        
        <div className="space-y-3 mb-4">
          <div className="flex items-start">
            <span className="font-bold mr-2">1.</span>
            <span className="flex-1 border-b border-gray-400 min-h-[20px]"></span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">2.</span>
            <span className="flex-1 border-b border-gray-400 min-h-[20px]"></span>
          </div>
          <div className="flex items-start">
            <span className="font-bold mr-2">3.</span>
            <span className="flex-1 border-b border-gray-400 min-h-[20px]"></span>
          </div>
        </div>

        <div className="flex justify-end items-center mt-6">
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1">Semnătură Client:</p>
            <div className="border-b border-gray-400 w-48 mb-1"></div>
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div className="flex justify-between items-end mt-8 pt-4 border-t">
        <div className="text-center">
          <p className="text-xs text-gray-600 mb-8">Semnătura Intermediar de Credite</p>
          <div className="border-b border-gray-400 w-48"></div>
          <p className="text-xs text-gray-500 mt-1">{intermediaryData.companyName}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-600 mb-8">Semnătura Client</p>
          <div className="border-b border-gray-400 w-48"></div>
          <p className="text-xs text-gray-500 mt-1">{clientData.fullName}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t text-center text-xs text-gray-500">
        <p>Document generat pe data de {generationDate} prin platforma FINBRO</p>
        <p>{intermediaryData.companyName} - Intermediar de Credite | {intermediaryData.phone} | {intermediaryData.email}</p>
      </div>
    </div>
  );
};

export default OfferPDFPreview;
